import React, { useState, useEffect, useContext } from "react";

import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";

import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	CircularProgress,
	Select,
	InputLabel,
	MenuItem,
	FormControl,
	TextField,
	InputAdornment,
	IconButton,
	FormControlLabel,
	Switch
} from '@material-ui/core';

import { Visibility, VisibilityOff } from '@material-ui/icons';

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";

import { i18n } from "../../translate/i18n";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import usePlans from "../../hooks/usePlans";

const useStyles = makeStyles(theme => ({
	root: {
		display: "flex",
		flexWrap: "wrap",
	},
	multFieldLine: {
		display: "flex",
		"& > *:not(:last-child)": {
			marginRight: theme.spacing(1),
		},
	},

	btnWrapper: {
		position: "relative",
	},

	buttonProgress: {
		color: green[500],
		position: "absolute",
		top: "50%",
		left: "50%",
		marginTop: -12,
		marginLeft: -12,
	},
	formControl: {
		margin: theme.spacing(1),
		minWidth: 120,
	},
}));

const normalizeCompanyFormValues = (data = {}) => ({
	name: data.name ?? "",
	email: data.email ?? "",
	passwordDefault: "",
	numberAttendants: data.numberAttendants ?? 1,
	numberConections: data.numberConections ?? 1,
	status: data.status === undefined ? true : Boolean(data.status),
	planId: data.planId ?? data.plan?.id ?? "",
});

const getCompanySchema = (isEdit) =>
	Yup.object().shape({
		name: Yup.string()
			.min(2, "Too Short!")
			.max(50, "Too Long!")
			.required("Nome é obrigatório"),
		email: Yup.string()
			.nullable()
			.transform((value) => (value == null ? "" : value))
			.trim()
			.email("Email é inválido")
			.required("E-mail é obrigatório"),
		passwordDefault: isEdit
			? Yup.string()
			: Yup.string().required("Senha é obrigatória"),
		planId: Yup.mixed().required("Plan es obligatorio"),
		numberAttendants: Yup.number(),
		numberConections: Yup.number(),
	});

const CompanyModal = ({ open, onClose, companyId }) => {
	const classes = useStyles();
	const { list: listPlans } = usePlans();

	const initialState = {
		name: "",
		email: "",
		passwordDefault: "",
		numberAttendants: 1,
		numberConections: 1,
		status: true,
		planId: "",
	};

	const [company, setCompany] = useState(initialState);
	const [showPassword, setShowPassword] = useState(false);
	const [plans, setPlans] = useState([]);

	useEffect(() => {
		const loadPlans = async () => {
			try {
				const list = await listPlans();
				setPlans(Array.isArray(list) ? list : []);
			} catch (err) {
				toastError(err);
			}
		};

		if (open) {
			loadPlans();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open]);

	useEffect(() => {
		const fetchCompany = async () => {
			if (!companyId) {
				setCompany(initialState);
				return;
			}
			try {
				const { data } = await api.get(`/companies/listPlan/${companyId}`);
				setCompany(normalizeCompanyFormValues(data));
			} catch (err) {
				toastError(err);
			}
		};

		if (open) {
			fetchCompany();
		}
	}, [companyId, open]);

	const handleClose = () => {
		onClose();
		setCompany(initialState);
	};

	const handleSaveCompany = async values => {
		const companyData = {
			id: companyId,
			name: values.name,
			email: values.email?.trim() || "",
			status: values.status,
			planId: Number(values.planId),
		};

		if (values.passwordDefault?.trim()) {
			companyData.password = values.passwordDefault;
		}

		try {
			if (companyId) {
				await api.put(`/companies/${companyId}`, companyData);
			} else {
				await api.post("/companies", {
					...companyData,
					password: values.passwordDefault,
				});
			}
			toast.success(i18n.t("companyModal.success"));
		} catch (err) {
			toastError(err);
		}
		handleClose();
	};

	return (
		<div className={classes.root}>
			<Dialog
				open={open}
				onClose={handleClose}
				maxWidth="xs"
				fullWidth
				scroll="paper"
			>
				<DialogTitle id="form-dialog-title">
					{companyId
						? `${i18n.t("companyModal.title.edit")}`
						: `${i18n.t("companyModal.title.add")}`}
				</DialogTitle>
				<Formik
					initialValues={company}
					enableReinitialize={true}
					validationSchema={getCompanySchema(Boolean(companyId))}
					onSubmit={(values, actions) => {
						setTimeout(() => {
							handleSaveCompany(values);
							actions.setSubmitting(false);
						}, 400);
					}}
				>
					{({ values, touched, errors, isSubmitting }) => (
						<Form>
							<DialogContent dividers>
								<div className={classes.multFieldLine}>
									<Field
										as={TextField}
										label={i18n.t("companyModal.form.name")}
										autoFocus
										name="name"
										error={touched.name && Boolean(errors.name)}
										helperText={touched.name && errors.name}
										variant="outlined"
										margin="dense"
										fullWidth
									/>
								</div>
								<div className={classes.multFieldLine}>
									<FormControlLabel
										control={
											<Field
												as={Switch}
												color="primary"
												name="status"
												checked={values.status}
											/>
										}
										label={"Ativo"}
									/>
								</div>
								<div className={classes.multFieldLine}>
									<Field
										as={TextField}
										label={i18n.t("companyModal.form.email")}
										name="email"
										error={touched.email && Boolean(errors.email)}
										helperText={touched.email && errors.email}
										variant="outlined"
										margin="dense"
										fullWidth
									/>
								</div>
								<div className={classes.multFieldLine}>
									<FormControl
										variant="outlined"
										margin="dense"
										fullWidth
										error={touched.planId && Boolean(errors.planId)}
									>
										<InputLabel id="company-plan-label">
											{i18n.t("companyModal.form.plan")}
										</InputLabel>
										<Field
											as={Select}
											labelId="company-plan-label"
											label={i18n.t("companyModal.form.plan")}
											id="company-plan"
											name="planId"
										>
											{plans.map((plan) => (
												<MenuItem key={plan.id} value={plan.id}>
													{plan.name}
													{plan.useFacebook || plan.useInstagram
														? " (Meta)"
														: ""}
												</MenuItem>
											))}
										</Field>
										{touched.planId && errors.planId && (
											<div style={{ color: "#f44336", fontSize: 12, marginTop: 4 }}>
												{errors.planId}
											</div>
										)}
									</FormControl>
								</div>
								<div className={classes.multFieldLine}>
									<Field
										as={TextField}
										name="passwordDefault"
										variant="outlined"
										margin="dense"
										label={i18n.t("companyModal.form.passwordDefault")}
										error={touched.passwordDefault && Boolean(errors.passwordDefault)}
										helperText={
											touched.passwordDefault && errors.passwordDefault
												? errors.passwordDefault
												: companyId
													? i18n.t("companyModal.form.passwordOptional")
													: ""
										}
										type={showPassword ? 'text' : 'password'}
										InputProps={{
											endAdornment: (
												<InputAdornment position="end">
													<IconButton
														aria-label="toggle password visibility"
														onClick={() => setShowPassword((e) => !e)}
													>
														{showPassword ? <VisibilityOff /> : <Visibility />}
													</IconButton>
												</InputAdornment>
											)
										}}
										fullWidth
									/>
								</div>
								
								{/* Campos comentados, mantidos como no original */}
								{/* <div className={classes.multFieldLine}>
									<Field
										as={TextField}
										label={i18n.t("companyModal.form.numberAttendants")}
										name="numberAttendants"
										error={touched.numberAttendants && Boolean(errors.numberAttendants)}
										helperText={touched.numberAttendants && errors.numberAttendants}
										variant="outlined"
										margin="dense"
										type="number"
										fullWidth
										style={
											// console.log('touched', touched)
											console.log('value', values)
										}
									/>
								</div> */}
								{/* <div className={classes.multFieldLine}>
									<Field
										as={TextField}
										label={i18n.t("companyModal.form.numberConections")}
										name="numberConections"
										error={touched.numberConections && Boolean(errors.numberConections)}
										helperText={touched.numberConections && errors.numberConections}
										variant="outlined"
										margin="dense"
										type="number"
										fullWidth
									/>
								</div> */}
							</DialogContent>
							<DialogActions>
								<Button
									onClick={handleClose}
									color="secondary"
									disabled={isSubmitting}
									variant="outlined"
								>
									{i18n.t("companyModal.buttons.cancel")}
								</Button>
								<Button
									type="submit"
									color="primary"
									disabled={isSubmitting}
									variant="contained"
									className={classes.btnWrapper}
								>
									{companyId
										? `${i18n.t("companyModal.buttons.okEdit")}`
										: `${i18n.t("companyModal.buttons.okAdd")}`}
									{isSubmitting && (
										<CircularProgress
											size={24}
											className={classes.buttonProgress}
										/>
									)}
								</Button>
							</DialogActions>
						</Form>
					)}
				</Formik>
			</Dialog>
		</div>
	);
};

export default CompanyModal;